// src/components/PipelineAnalysis/PipelineAnalysis.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import UploadModal from "./upload-modal";
import ParameterSelection from "./parameter-selection";
import './pipeline-analysis.css';
import { getApiEndpoint } from '../../config/oncorisk-config';
import { getCookieId } from '../../WebserverUser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileArrowUp, faFileExcel, faFileExport } from '@fortawesome/free-solid-svg-icons';

const POLLING_INTERVAL_MS = 5000; // Poll every 5 seconds
const PROGRESS_SIMULATION_DURATION_MS = 5 * 60 * 1000; // 5 minutes for 0-90%

function PipelineAnalysis() {
  const [activeTab, setActiveTab] = useState('jobs');
  const [showJobsList, setShowJobsList] = useState(true);
  const [showReportViewer, setShowReportViewer] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [cookieId, setCookieId] = useState('');
  const [isLoadingJobs, setIsLoadingJobs] = useState(true);
  const [isLoadingJobDetails, setIsLoadingJobDetails] = useState(false); // Separate state for job details
  const [errorLoadingJobs, setErrorLoadingJobs] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Detail tabs and report generation states
  const [detailTab, setDetailTab] = useState('parameters');
  const [reportCompany, setReportCompany] = useState('Report Preview');
  const [reportLanguage, setReportLanguage] = useState('English');
  const [reportStatus, setReportStatus] = useState(null);
  const [reportUuid, setReportUuid] = useState(null);
  const [reportUrl, setReportUrl] = useState(null);
  const reportPollRef = useRef(null);

  const [paramSearch, setParamSearch] = useState('');
  const [resultsSearch, setResultsSearch] = useState('');
  const [resultsTypeFilter, setResultsTypeFilter] = useState('all');

  // Dummy row action handlers for tables
  const handleRemoveFile = (index) => {
    console.log('[Parameters] remove row', index);
  };

  const handleMoveFileUp = (index) => {
    console.log('[Parameters] move row up', index);
  };

  const handleMoveFileDown = (index) => {
    console.log('[Parameters] move row down', index);
  };

  const handleRemoveResult = (index) => {
    console.log('[Results] remove row', index);
  };

  const handleMoveResultUp = (index) => {
    console.log('[Results] move row up', index);
  };

  const handleMoveResultDown = (index) => {
    console.log('[Results] move row down', index);
  };

  const handleEditTier = (index, current) => {
    const val = prompt('Edit tier', current);
    if (val !== null) {
      console.log('[Results] edit tier', index, val);
    }
  };

  // For forcing re-render to update progress bars
  const [, setTick] = useState(0); 

  useEffect(() => {
    const id = getCookieId();
    console.log('[PipelineAnalysis] cookie from webserverUser:', id);
    if (id) {
      setCookieId(id);
    } else {
      console.warn('[PipelineAnalysis] No cookieId found.');
      setErrorLoadingJobs('User cookie not found. Cannot load analysis jobs.');
      setIsLoadingJobs(false);
    }
  }, []);

  // cleanup polling when component unmounts
  useEffect(() => {
    return () => {
      if (reportPollRef.current) {
        clearInterval(reportPollRef.current);
      }
    };
  }, []);

  const fetchJobs = useCallback(async (currentCookieId, specificJobId = null, isPolling = false) => {
    if (!currentCookieId) return;
    
    const endpoint = getApiEndpoint('fetchAnalysisJob');
    const body = { cookieId: currentCookieId };
    if (specificJobId) {
      body.jobId = specificJobId;
    }

    try {
      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({ error: 'Failed to parse error response' }));
        console.error(`fetchAnalysisJob failed with status ${resp.status}:`, errorData.error || resp.statusText);
        if (!specificJobId && !isPolling) setErrorLoadingJobs(`Error loading jobs: ${errorData.error || resp.statusText}`);
        return null;
      }

      const data = await resp.json();
      console.log(`[PipelineAnalysis] Jobs data received (jobId: ${specificJobId || 'all'}):`); 
      // Log full job details but in a cleaner format for debugging
      if (specificJobId) {
        console.log('Job details:', {
          uuid: data.uuid,
          name: data.name,
          status: data.status,
          patientId: data.patientId,
          initiated: data.pipeline_initiated_time,
          completed: data.pipelines_completed_time,
          error: data.error
        });
      } else if (Array.isArray(data)) {
        console.log(`Received ${data.length} jobs`);
      }
      return data;

    } catch (err) {
      console.error(`fetchAnalysisJob error (jobId: ${specificJobId || 'all'}):`, err);
      if (!specificJobId && !isPolling) setErrorLoadingJobs(`Network error: ${err.message}`);
      return null;
    }
  }, []);

  // Initial job load and polling
  // Initial job load and polling
  useEffect(() => {
    if (!cookieId) return;

    let isMounted = true;
    let pollTimeoutId = null;
    let isInitialLoad = true;

    const loadAndPoll = async () => {
      if (!isMounted) return;

      if (isInitialLoad) {
        setIsLoadingJobs(true);
      }

      const fetchedJobsData = await fetchJobs(cookieId, null, !isInitialLoad);

      if (!isMounted) return;

      if (isInitialLoad) {
        setIsLoadingJobs(false);
        isInitialLoad = false;
      }

      if (fetchedJobsData && Array.isArray(fetchedJobsData)) {
        const hasJobsChanged = JSON.stringify(fetchedJobsData) !== JSON.stringify(jobs);

        if (hasJobsChanged) {
          console.log('[PipelineAnalysis] Jobs list updated');
          setJobs(fetchedJobsData);
          setErrorLoadingJobs(null);

          if (fetchedJobsData.length > 0) {
            const currentSelectedExists = selectedJob && fetchedJobsData.find(j => j.uuid === selectedJob.uuid);

            if (!currentSelectedExists) {
              // MODIFIED BLOCK STARTS HERE
              // If a job *was* selected, but it's not in the new list (or list is empty), deselect it.
              // If no job was selected initially (selectedJob was null), selectedJob remains null (no auto-selection).
              if (selectedJob) { // This condition is true only if a job WAS selected but is now gone from the list
                  console.log('[PipelineAnalysis] Previously selected job no longer in list. Deselecting.');
                  setSelectedJob(null);
              }
              // If selectedJob was initially null, this 'if (selectedJob)' is false, and selectedJob remains null.
              // MODIFIED BLOCK ENDS HERE
            } else if (selectedJob) {
              // Re-fetch details for the currently selected job to keep it fresh
              const updatedSelectedJobDetails = await fetchJobs(cookieId, selectedJob.uuid, true);
              if (isMounted && updatedSelectedJobDetails) {
                if (JSON.stringify(updatedSelectedJobDetails) !== JSON.stringify(selectedJob)) {
                  console.log('[PipelineAnalysis] Selected job details updated');
                  setSelectedJob(updatedSelectedJobDetails);
                }
              }
            }
          } else if (selectedJob) { // If fetchedJobsData becomes empty
            console.log('[PipelineAnalysis] Jobs list is now empty. Deselecting job.');
            setSelectedJob(null);
          }
        }
      } else if (fetchedJobsData === null && jobs.length === 0 && isInitialLoad) {
        // Error already set by fetchJobs
      }

      if (isMounted) {
        pollTimeoutId = setTimeout(loadAndPoll, POLLING_INTERVAL_MS);
      }
    };

    loadAndPoll();

    return () => {
      isMounted = false;
      if (pollTimeoutId) clearTimeout(pollTimeoutId);
    };
  }, [cookieId, fetchJobs]);
  // Effect for progress bar updates
  useEffect(() => {
      const hasRunningJobs = jobs.some(job => job.status === 'running');
      if (!hasRunningJobs) return;

      const intervalId = setInterval(() => {
          setTick(prevTick => prevTick + 1); // Force re-render
      }, 1000); // Update progress display every second

      return () => clearInterval(intervalId);
  }, [jobs]); // Rerun when jobs array changes





  const calculateFrontendProgress = useCallback((job) => {
    if (!job) return 0;

    const { status, pipeline_initiated_time, pipelines_completed_time } = job;

    if (status === 'completed') return 100;
    if (status === 'queued' || !pipeline_initiated_time) return 0;

    const initiatedTime = new Date(pipeline_initiated_time).getTime();
    if (isNaN(initiatedTime)) return 0;

    if (status === 'running') {
        const elapsed = Date.now() - initiatedTime;
        const calculatedProgress = (elapsed / PROGRESS_SIMULATION_DURATION_MS) * 100;
        return Math.min(90, Math.max(0, Math.round(calculatedProgress)));
    }

    if (status === 'failed') {
        if (pipelines_completed_time) { // This is the failure time
            const failureTime = new Date(pipelines_completed_time).getTime();
            if (!isNaN(failureTime)) {
                const elapsedAtFailure = failureTime - initiatedTime;
                const calculatedProgress = (elapsedAtFailure / PROGRESS_SIMULATION_DURATION_MS) * 100;
                return Math.min(100, Math.max(0, Math.round(calculatedProgress)));
            }
        }
        return 0; 
    }
    return 0;
  }, []);

  const getStatusColor = (status) => {
    const statusColors = {
      completed: '#4caf50',
      running: '#2196f3',
      failed: '#f44336',
      queued: '#ff9800',
      pending: '#ff9800', // Map 'pending' from DB to 'queued' color
    };
    return statusColors[status] || '#a0a0a0';
  };

  const handleJobSelect = async (job) => {
    setIsLoadingJobDetails(true);
    const detailedJobData = await fetchJobs(cookieId, job.uuid);
    setIsLoadingJobDetails(false);
    if (detailedJobData) {
      setSelectedJob(detailedJobData);
    } else {
      setSelectedJob(job);
      console.error(`Failed to fetch details for job ${job.uuid}. Displaying summary.`);
    }
    setDetailTab('parameters'); // Default to parameters tab
    setReportStatus(null);
    setReportUuid(null);
    setReportUrl(null);
    setParamSearch('');
    if (reportPollRef.current) {
      clearInterval(reportPollRef.current);
      reportPollRef.current = null;
    }
  };
  const handleDetailTabChange = async (newTab) => {
    setDetailTab(newTab);
    if (newTab === 'parameters' && selectedJob) {
      console.log('[PipelineAnalysis] Switched to Parameters tab, re-fetching selected job details.');
      setIsLoadingJobDetails(true); // Show loading indicator
      const detailedJobData = await fetchJobs(cookieId, selectedJob.uuid);
      setIsLoadingJobDetails(false);
      if (detailedJobData) {
        setSelectedJob(detailedJobData); // This will pass fresh resultsData to ParameterSelection
      } else {
        // Handle error or keep existing selectedJob if fetch fails
        console.error(`Failed to re-fetch details for job ${selectedJob.uuid} on tab switch.`);
      }
    }
  };
const pollReportStatus = useCallback(async (preset) => {
  // Construct the URL to fetch the report status/PDF
  const reportFetchUrl = `https://service1.szapfs.org/UPG-retrieve-report?Preset=${encodeURIComponent(preset)}&ReportType=OncoRiskPro`;
  
  try {
    console.log('[ReportGeneration] polling status for preset:', preset, 'URL:', reportFetchUrl);
    const resp = await fetch(reportFetchUrl);

    if (resp.ok) {
      const contentType = resp.headers.get("content-type");
      if (contentType && contentType.toLowerCase().includes("application/pdf")) {
        console.log('[ReportGeneration] PDF received directly for preset:', preset); // Log preset
        if (reportPollRef.current) {
          clearInterval(reportPollRef.current);
          reportPollRef.current = null;
        }
        setReportStatus('Done');
        setReportUuid(preset); 
        setReportUrl(reportFetchUrl);
        return; 
      } else {
        const data = await resp.json().catch(() => ({ status: 'Pending', error: 'Unexpected 200 OK non-PDF response' }));
        console.log('[ReportGeneration] (Non-PDF 200 OK) status response for preset:', preset, data); // Log preset
        
        if (data.status === 'Done' && data.uuid) {
          clearInterval(reportPollRef.current);
          reportPollRef.current = null;
          setReportStatus('Done');
          setReportUuid(data.uuid);
          setReportUrl(`https://service1.szapfs.org/reports/${data.uuid}.pdf`); 
        } else if (data.error === 'Report not found on disk yet' || data.status === 'Pending' || data.status === 'Checking') { // Added more pending states
           setReportStatus('Pending');
        } else {
          setReportStatus(data.status || 'Pending');
        }
        return;
      }
    } else {
      const errorData = await resp.json().catch(() => ({ error: `Status poll failed: ${resp.status}` }));
      console.log('[ReportGeneration] Status poll non-OK response for preset:', preset, 'Status:', resp.status, errorData); // Log preset

      if (resp.status === 404 && errorData.error === 'Report not found on disk yet') {
        setReportStatus('Pending');
      } else {
        setReportStatus(`Error: ${errorData.error || resp.statusText}`);
        if (reportPollRef.current) {
          clearInterval(reportPollRef.current);
          reportPollRef.current = null;
        }
      }
      return;
    }
  } catch (err) {
    console.error('[ReportGeneration] Polling network/fetch/parse error for preset:', preset, err); // Log preset
    setReportStatus('Error during polling');
  }
}, [setReportStatus, setReportUuid, setReportUrl]); // --- ADDED useCallback dependencies ---
// REPLACE THIS ENTIRE FUNCTION
const handleReportSubmit = async () => {
  if (!selectedJob || !selectedJob.patientId) { // Added check for patientId
    console.error("[ReportGeneration] No selected job or patientId to submit report for.");
    setReportStatus("Error: Missing job info");
    return;
  }

  const presetForReport = selectedJob.patientId; // Use patientId

  const payload = {
    Preset: presetForReport, // Use presetForReport
    ReportType: 'OncoRiskPro',
    Language: reportLanguage,
    Company: reportCompany,
  };

  console.log('[ReportGeneration] submitting report for Preset:', presetForReport, payload);

  // --- NEW ---
  setReportUrl(null); // Clear current PDF
  setReportStatus('Submitting...'); // Show submitting/generating state
  setReportUuid(presetForReport); // Associate with this specific submission

  if (reportPollRef.current) { // Clear any old poll
    clearInterval(reportPollRef.current);
    reportPollRef.current = null;
  }
  // --- END NEW ---

  try {
    const resp = await fetch('https://service1.szapfs.org/UPG-regeneratereport', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!resp.ok) {
      const errorData = await resp.json().catch(() => ({ message: 'Submission failed, unknown server error.' })); // Improved error parsing
      console.error('[ReportGeneration] submit failed', resp.status, errorData);
      setReportStatus(`Error: ${errorData.message || resp.statusText}`); // Show parsed error
      return;
    }
    const data = await resp.json().catch(() => ({}));
    console.log('[ReportGeneration] submit successful, response:', data);
    
    setReportStatus('Pending'); // Explicitly set to Pending after successful trigger

    // Start polling for THIS specific preset
    pollReportStatus(presetForReport); // Poll once immediately
    reportPollRef.current = setInterval(() => pollReportStatus(presetForReport), 1000); // Use presetForReport
  } catch (err) {
    console.error('[ReportGeneration] submit network error', err);
    setReportStatus('Error: Network Issue during submission'); // More specific error
  }
};
  // Automatically check for report when a job completes
  const handleExportClick = () => {
    if (!selectedJob?.resultsData?.length) return;
  
    const data = selectedJob.resultsData;
    const headers = Object.keys(data[0]);
    const csvRows = [
      headers.join(','), 
      ...data.map(row =>
        headers.map(field =>
          `"${String(row[field] ?? '').replace(/"/g, '""')}"`
        ).join(',')
      )
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedJob.patientId || selectedJob.uuid}-results.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
useEffect(() => {
  const presetForReport = selectedJob?.patientId;

  if (!selectedJob || !presetForReport) {
    // Cleanup is mostly handled by handleJobSelect, but good to have a bail-out
    if (reportPollRef.current) {
      clearInterval(reportPollRef.current);
      reportPollRef.current = null;
    }
    // Ensure report state is null if no job is selected
    // setReportStatus(null); // This is done in handleJobSelect
    // setReportUrl(null);   // This is done in handleJobSelect
    // setReportUuid(null);  // This is done in handleJobSelect
    return;
  }

  if (selectedJob.status === 'completed') {
    // Conditions to START auto-polling for a COMPLETED job:
    // 1. We don't already have a 'Done' report for THIS preset.
    // AND
    // 2. We are not ALREADY actively polling (due to manual submit or previous auto-poll) for THIS preset.
    const isReportDoneForThisPreset = (reportStatus === 'Done' && reportUrl && reportUuid === presetForReport);
    const isCurrentlyPollingForThisPreset = (reportPollRef.current && reportUuid === presetForReport && 
                                            (reportStatus === 'Pending' || reportStatus === 'Checking' || reportStatus === 'Submitting...'));

    if (!isReportDoneForThisPreset && !isCurrentlyPollingForThisPreset) {
      console.log('[ReportGeneration] Auto-checking for report for completed job, preset:', presetForReport);
      
      // Clear any poll that might be for a *different* preset (should be rare if handleJobSelect is working)
      if (reportPollRef.current && reportUuid !== presetForReport) {
          clearInterval(reportPollRef.current);
          reportPollRef.current = null;
      }

      setReportStatus('Checking');
      setReportUrl(null); // Clear any old PDF
      setReportUuid(presetForReport); // Set the ID we are checking for

      pollReportStatus(presetForReport); // Poll once immediately
      reportPollRef.current = setInterval(() => pollReportStatus(presetForReport), 1000); // Or your REPORT_POLLING_INTERVAL_MS
    }
  }
  // No explicit cleanup function needed here IF:
  // - handleJobSelect correctly clears polls when selectedJob changes.
  // - pollReportStatus clears its own interval on 'Done' or terminal 'Error'.
  // The main purpose of this effect is to *initiate* the auto-poll.
}, [selectedJob, pollReportStatus]); // --- CHANGED dependencies ---
  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      const matchesSearchTerm = job.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                job.patientId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                job.uuid?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
      return matchesSearchTerm && matchesStatus;
    });
  }, [jobs, searchTerm, statusFilter]);

  const filteredResults = useMemo(() => {
    if (!selectedJob || !selectedJob.resultsData) return [];
    return selectedJob.resultsData.filter(res => {
      const matchesType = resultsTypeFilter === 'all' || res.entry_type === resultsTypeFilter;
      const query = resultsSearch.toLowerCase();
      const matchesSearch = !query ||
        res.gene_symbol?.toLowerCase().includes(query) ||
        res.biomarker_description?.toLowerCase().includes(query);
      return matchesType && matchesSearch;
    });
  }, [selectedJob, resultsSearch, resultsTypeFilter]);

  const renderJobsTab = () => {
    if (isLoadingJobs) { // Only shows on initial load now
      return <div className="OCRP-loading-placeholder">  <i className="fas fa-spinner fa-spin"></i></div>;
    }
    if (errorLoadingJobs && jobs.length === 0) {
      return <div className="OCRP-error-placeholder">Error: {errorLoadingJobs}</div>;
    }

    return (
      <div className="OCRP-jobs-container">
        <div className={`OCRP-jobs-list-container ${!showJobsList ? 'OCRP-panel-collapsed' : ''}`}>
          <div className="OCRP-panel-header">
            {showJobsList && (
              <button
                className="OCRP-upload-open"
                onClick={() => setShowUploadModal(true)}
                title="Upload Files"
              >
                <i className="fas fa-upload"></i>
              </button>
            )}
            <h2>Analysis Jobs</h2>
            <i className="fas fa-tasks"></i>
          </div>
          <div className="OCRP-jobs-filter">
            <input 
              type="text" 
              placeholder="Search jobs by name, patient, UUID..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Statuses</option>
              <option value="running">Running</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="queued">Queued</option>
            </select>
          </div>
          <div className="OCRP-jobs-list">
            {filteredJobs.length === 0 && <div className='OCRP-no-jobs-placeholder'> No Jobs Available.</div>}
            {filteredJobs.map(job => {
              const progress = calculateFrontendProgress(job);
              return (
                <div
                  key={job.uuid}
                  className={`OCRP-job-item ${selectedJob && selectedJob.uuid === job.uuid ? 'OCRP-job-selected' : ''}`}
                  onClick={() => handleJobSelect(job)}
                >
                  <div className="OCRP-job-status">
                    <div className="OCRP-status-indicator" style={{ backgroundColor: getStatusColor(job.status) }}></div>
                  </div>
                  <div className="OCRP-job-details">
                    <h3>{job.name || `Job ${job.uuid.substring(0,8)}`}</h3>
                    <div className="OCRP-job-meta">
                      <span className="OCRP-job-patient">Patient/ID: {job.patientId}</span>
                      <span className="OCRP-job-type">{job.fileType}</span>
                    </div>
                    {job.status === 'running' && (
                      <div className="OCRP-job-progress">
                        <div className="OCRP-progress-bar">
                          <div className="OCRP-progress-fill" style={{ width: `${progress}%`, backgroundColor: getStatusColor(job.status) }}></div>
                        </div>
                        <span>{progress}%</span>
                      </div>
                    )}
                    <div className="OCRP-job-status-text" style={{ color: getStatusColor(job.status) }}>
                      {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="OCRP-job-details-container">
          {isLoadingJobDetails && <div className="OCRP-loading-placeholder">  <i className="fas fa-spinner fa-spin"></i></div>}
          {!isLoadingJobDetails && selectedJob ? (
            <>
              <div className="OCRP-job-header">
                <button
                  className="OCRP-panel-toggle OCRP-toggle-left"
                  onClick={() => setShowJobsList(!showJobsList)}
                  title={showJobsList ? 'Collapse jobs panel' : 'Expand jobs panel'}
                >
                  <i className={`fas fa-chevron-${showJobsList ? 'left' : 'right'}`}></i>
                </button>
                <h2>{selectedJob.name}</h2>
                <div className="OCRP-job-actions">
                  {/* Action buttons can be added here based on selectedJob.status */}
                  <button
                    className="OCRP-panel-toggle OCRP-toggle-right"
                    onClick={() => setShowReportViewer(!showReportViewer)}
                    title={showReportViewer ? 'Collapse report panel' : 'Expand report panel'}
                  >
                    <i className={`fas fa-chevron-${showReportViewer ? 'right' : 'left'}`}></i>
                  </button>
                </div>
              </div>

              <div className="OCRP-job-info-grid">
                <div className="OCRP-info-card">
                  <h4>Job UUID</h4>
                  <p>{selectedJob.uuid}</p>
                </div>
                <div className="OCRP-info-card">
                  <h4>Patient ID</h4>
                  <p>{selectedJob.patientId}</p>
                </div>
                <div className="OCRP-info-card">
                  <h4>Status</h4>
                  <p style={{ color: getStatusColor(selectedJob.status) }}>
                    {selectedJob.status.charAt(0).toUpperCase() + selectedJob.status.slice(1)}
                  </p>
                </div>
                <div className="OCRP-info-card">
                  <h4>Primary File Type</h4>
                  <p>{selectedJob.fileType}</p>
                </div>
                <div className="OCRP-info-card">
                  <h4>Initiated Time</h4>
                  <p>{selectedJob.pipeline_initiated_time ? new Date(selectedJob.pipeline_initiated_time).toLocaleString() : (selectedJob.upload_time ? `(Uploaded: ${new Date(selectedJob.upload_time).toLocaleString()})` : 'N/A')}</p>
                </div>
                {selectedJob.status === 'completed' && selectedJob.pipelines_completed_time && (
                  <div className="OCRP-info-card">
                    <h4>Completion Time</h4>
                    <p>{new Date(selectedJob.pipelines_completed_time).toLocaleString()}</p>
                  </div>
                )}
                {selectedJob.status === 'running' && selectedJob.estimatedCompletion && (
                  <div className="OCRP-info-card">
                    <h4>Est. Completion (Sim)</h4>
                    <p>{new Date(selectedJob.estimatedCompletion).toLocaleString()}</p>
                  </div>
                )}
                {selectedJob.status === 'failed' && (
                  <>
                    {selectedJob.pipelines_completed_time &&
                      <div className="OCRP-info-card">
                        <h4>Failure Time</h4>
                        <p>{new Date(selectedJob.pipelines_completed_time).toLocaleString()}</p>
                      </div>
                    }
                    <div className="OCRP-info-card OCRP-info-card-full-width"> {/* Make error card wider */}
                      <h4>Error Details</h4>
                      <p className="OCRP-error-text">{selectedJob.error || 'No specific error message provided.'}</p>
                    </div>
                  </>
                )}
                {selectedJob.status === 'queued' && (
                  <div className="OCRP-info-card">
                    <h4>Queue Position</h4>
                    <p>{selectedJob.queuePosition || 'N/A'}</p>
                  </div>
                )}
              </div>

              <div className="OCRP-tabs">
          <button
            className={`OCRP-tab-btn ${detailTab === 'parameters' ? 'OCRP-tab-active' : ''}`}
            onClick={() => handleDetailTabChange('parameters')} // USE THE NEW HANDLER
          >Parameters</button>
          <button
            className={`OCRP-tab-btn ${detailTab === 'report' ? 'OCRP-tab-active' : ''}`}
            onClick={() => handleDetailTabChange('report')} // USE THE NEW HANDLER
          >Report Generation</button>



<div className="OCRP-export-button" onClick={handleExportClick}>
          <FontAwesomeIcon icon={faFileExport} />

          </div>

        </div>

        {detailTab === 'parameters' && (
          <div className="OCRP-parameters-panel">
            <ParameterSelection
              selectedJob={selectedJob} // This selectedJob will now have fresh resultsData
              resultsData={selectedJob?.resultsData || []}
              resultsSearch={resultsSearch}
              setResultsSearch={setResultsSearch}
              resultsTypeFilter={resultsTypeFilter}
              setResultsTypeFilter={setResultsTypeFilter}
              // No onParameterUpdated needed for this approach
            />
          </div>
        )}

              {detailTab === 'report' && (
                <div className="OCRP-report-gen-panel">
                  <div className="OCRP-report-gen-form">
                    <div className="OCRP-bioinformatic-options">
                      <h3><i className="fas fa-dna"></i> Bioinformatic Options</h3>
                      <div className="OCRP-bioinformatic-grid">
                        <div className="OCRP-bioinformatic-option">
                          <input type="checkbox" id="bioopt1" />
                          <label htmlFor="bioopt1">Include OncoKB Data</label>
                        </div>
                        <div className="OCRP-bioinformatic-option">
                          <input type="checkbox" id="bioopt2" />
                          <label htmlFor="bioopt2">Include CIViC Data</label>
                        </div>
                        <div className="OCRP-bioinformatic-option">
                          <input type="checkbox" id="bioopt3" />
                          <label htmlFor="bioopt3">Include Supplementary Figures</label>
                        </div>
                        <div className="OCRP-bioinformatic-option">
                          <input type="checkbox" id="bioopt4" />
                          <label htmlFor="bioopt4">Include Supplementary Tables</label>
                        </div>
                      </div>
                    </div>
                    
                    <div className="OCRP-form-divider">
                      <span>Optional Parameters</span>
                    </div>
                    
                    <div className="OCRP-optional-params">
                      <div className="OCRP-report-gen-field">
                        <label>Company</label>
                        <select value={reportCompany} onChange={e => setReportCompany(e.target.value)}>
                          <option value="Report Preview">Report Preview</option>
                          <option value="SZA Longevity">SZA Longevity</option>
                          <option value="Silk Longevity">Silk Longevity</option>
                          <option value="Acibadem">Acibadem</option>
                        </select>
                      </div>
                      <div className="OCRP-report-gen-field">
                        <label>Language</label>
                        <select value={reportLanguage} onChange={e => setReportLanguage(e.target.value)}>
                          <option value="English">English</option>
                          <option value="Turkish">Turkish</option>
                          <option value="Korean">Korean</option>
                          <option value="Japanese">Japanese</option>
                          <option value="Chinese">Chinese</option>

                        </select>
                      </div>
                    </div>
                    
                    <button className="OCRP-button OCRP-button-primary" onClick={handleReportSubmit}>
                      <i className="fas fa-file-medical"></i> Generate Report
                    </button>
                  </div>
                  {reportStatus && <p className="OCRP-report-status">Status: {reportStatus}</p>}
                  {reportUuid && <p className="OCRP-report-status">UUID: {reportUuid}</p>}
                </div>
              )}

            </>
          ) : (
            !isLoadingJobDetails && <div className="OCRP-no-job-selected">
              <i className="fas fa-tasks"></i>
              <p>Select a job to view details</p>
            </div>
          )}
        </div>

        <div className={`OCRP-report-viewer ${!showReportViewer ? 'OCRP-panel-collapsed' : ''}`}>
          <div className="OCRP-panel-header">
            <h2>Report Preview</h2>
            <i className="fas fa-file-pdf"></i>
          </div>
          <div className="OCRP-report-placeholder">
            <div className="OCRP-report-paper">
              {reportUrl ? (
                <div className="OCRP-report-iframe-container">
                  <iframe 
                    src={reportUrl} 
                    title="Report" 
                    className="OCRP-report-iframe"
                  />
                </div>
              ) : (
                <div className="OCRP-report-message">
                  {(reportStatus === 'Checking' || reportStatus === 'Pending') && (
                    <div className="OCRP-report-loader">
                      <div className="OCRP-report-shimmer"></div>
                      <div className="OCRP-report-progress-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  )}
                  <div className="OCRP-report-icon">
                    <i className="fas fa-file-medical-alt"></i>
                  </div>
                  <p className="OCRP-report-status-text">
                    {reportStatus === 'Checking' || reportStatus === 'Pending'
                      ? 'Generating report...'
                      : ''}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 300);
  }, [showJobsList, showReportViewer]);

  return (
    <div className="OCRP-pipeline-analysis">
      <div className="OCRP-analysis-tabs">
        <button
          className={`OCRP-tab-button ${activeTab === 'jobs' ? 'OCRP-tab-active' : ''}`}
          onClick={() => setActiveTab('jobs')}
        >
          <i className="fas fa-tasks"></i>
          <span>Analysis Jobs</span>
        </button>
        <button
          className={`OCRP-tab-button ${activeTab === 'settings' ? 'OCRP-tab-active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <i className="fas fa-cog"></i>
          <span>Pipeline Settings</span>
        </button>
      </div>

      <div className="OCRP-analysis-content">
        {activeTab === 'jobs' && renderJobsTab()}
        {activeTab === 'settings' && (
          <div className="OCRP-settings-placeholder">
            <i className="fas fa-cog"></i>
            <h3>Pipeline Settings</h3>
            <p>Configure analysis parameters and pipeline behavior</p>
          </div>
        )}
      </div>
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        cookieId={cookieId}
        onUploadSuccess={() => fetchJobs(cookieId).then(data => data && Array.isArray(data) && setJobs(data))} // Refresh jobs list on successful upload
      />
    </div>
  );
}

export default PipelineAnalysis;